import type { ReactNode } from 'react';

import { formatarData } from '@/nucleo/formato/datas';

import { rotuloDosTipos } from '../catalogo-veiculos/rotulos';
import type { ChaveDeCadastro, GrupoDeCadastro, LinhaDoCadastro } from './cadastros.contrato';

/**
 * Como a tela de cada cadastro de referência se apresenta: título, colunas da
 * lista e campos do formulário. O dado e a regra vêm da API; aqui só mora o que
 * a pessoa lê.
 *
 * AS COLUNAS SEGUEM O PROTÓTIPO, no nome e na ordem — e, onde o banco tem um
 * campo que o protótipo não mostra na lista (marca do modelo, faixa de lugares,
 * exigência de aprovação, meio de publicação), a coluna extra vem DEPOIS das do
 * protótipo. `paridade.spec.ts` confere as duas coisas.
 */

const CADASTROS = '/admin/parametros/cadastros';
const REFERENCIA = '/admin/parametros/referencia';

export interface ColunaDoCadastro {
  titulo: string;
  campo: string | ((linha: LinhaDoCadastro) => ReactNode);
  numerica?: boolean;
  /**
   * Chave que o servidor entende para ordenar (uma chave do cadastro, ou
   * `situacao`). Sem ela, a coluna derivada não ordena — melhor não ordenar do
   * que ordenar errado.
   */
  ordenarPor?: string;
}

export interface OpcaoDeCampo {
  valor: string;
  texto: string;
}

export interface CampoDeFormulario {
  nome: string;
  /** Rótulo como o protótipo o escreve, em caixa normal. */
  rotulo: string;
  /** Bloco do formulário (`Identificação`, `Observações`). */
  bloco: string;
  obrigatorio?: boolean;
  /** Dica curta abaixo do campo. Diz o formato, não repete o rótulo. */
  ajuda?: string;
  /**
   * `marca` e `modelo`: seletor com os itens ativos do catálogo de veículo.
   * `grafias`: lista de grafias equivalentes, uma por linha.
   */
  tipo?:
    | 'text'
    | 'number'
    | 'email'
    | 'textarea'
    | 'select'
    | 'checkbox'
    | 'marca'
    | 'modelo'
    | 'grafias';
  opcoes?: readonly OpcaoDeCampo[];
  /** Rótulo ao lado da caixa de marcação, quando `tipo` é `checkbox`. */
  rotuloDaMarcacao?: string;
  maxLength?: number;
  /** Número: limites aceitos. */
  min?: number;
  max?: number;
}

export interface CampoDeDetalhe {
  rotulo: string;
  campo: string | ((linha: LinhaDoCadastro) => ReactNode);
}

export interface DefinicaoDoCadastro {
  chave: ChaveDeCadastro;
  grupo: GrupoDeCadastro;
  titulo: string;
  descricao: string;
  /** Singular, para os títulos do formulário e as mensagens: "Marca". */
  nomeDaEntidade: string;
  /** Artigo e concordância: `a` para "a marca", `o` para "o modelo". */
  genero: 'a' | 'o';
  caminho: string;
  hub: string;
  rotuloDoHub: string;
  colunas: readonly ColunaDoCadastro[];
  campos: readonly CampoDeFormulario[];
  /** Colunas `Ordem` e `Saúde`, que o protótipo tem em toda tabela de domínio. */
  colunasDeControle: boolean;
  /** Escolas: só consulta. Sem `Novo`, sem `Ações`, sem formulário. */
  somenteConsulta?: boolean;
  /** Seguradoras e escolas têm tela de detalhe, aberta pelo slug. */
  comDetalhe?: boolean;
  /** Aviso informativo acima da tabela. Só afirma o que o sistema de fato faz. */
  aviso?: string;
  situacaoFeminina?: boolean;
  /** Campos da tela de detalhe. */
  detalhe?: { titulo: string; campos: readonly CampoDeDetalhe[] };
  /**
   * Marcas, modelos e versões: sincronizados com a fonte oficial. A tela traz
   * o botão "Como funciona a integração" e, no formulário, o que a fonte diz
   * do item.
   */
  integracaoComFonte?: boolean;
}

export const ROTULO_DA_GRAVIDADE: Readonly<Record<string, string>> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
  CRITICA: 'Crítica',
};

export const ROTULO_DO_RECURSO: Readonly<Record<string, string>> = {
  VEICULO: 'Veículo',
  CONDUTOR: 'Condutor',
  MONITOR: 'Monitor',
  TODOS: 'Todos',
};

export const ROTULO_DO_PUBLICO: Readonly<Record<string, string>> = {
  CONDUTOR: 'Condutor',
  MONITOR: 'Monitor',
  AMBOS: 'Condutor e monitor',
};

const emOpcoes = (rotulos: Readonly<Record<string, string>>): OpcaoDeCampo[] =>
  Object.entries(rotulos).map(([valor, texto]) => ({ valor, texto }));

/** Booleano vira palavra; ausente vira travessão. Número e texto saem como são. */
export function formatarValor(valor: unknown): string {
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (valor === null || valor === undefined || valor === '') return '—';
  return String(valor);
}

const pelaTabela =
  (rotulos: Readonly<Record<string, string>>) =>
  (campo: string) =>
  (linha: LinhaDoCadastro): string => {
    const valor = linha[campo];
    return typeof valor === 'string' ? (rotulos[valor] ?? valor) : '—';
  };

const rotuloDe = (tabela: Readonly<Record<string, string>>, campo: string) =>
  pelaTabela(tabela)(campo);

function lugares(linha: LinhaDoCadastro): string {
  const min = linha.capacidadeMin;
  const max = linha.capacidadeMax;
  if (typeof min === 'number' && typeof max === 'number') return `${min} a ${max}`;
  if (typeof min === 'number') return `${min} ou mais`;
  if (typeof max === 'number') return `até ${max}`;
  return '—';
}

function cor(linha: LinhaDoCadastro): ReactNode {
  const hex = linha.cor;
  if (typeof hex !== 'string' || hex === '') return '—';
  return (
    <span className="inline-flex items-center gap-2">
      {/* A cor do veículo é dado do cadastro, não cor do tema: por isso o estilo em linha. */}
      <span
        aria-hidden="true"
        className="inline-block h-3 w-3 rounded-full border border-border"
        style={{ backgroundColor: hex }}
      />
      {hex}
    </span>
  );
}

const NOME = { campo: 'nome', titulo: 'Nome', ordenarPor: 'nome' } as const;

// ---- Marcas, modelos e versões de veículo, sincronizados com a fonte oficial ----

/** O que a fonte oficial diz do item: visto no último arquivo, ou desde quando não aparece. */
export function situacaoNaFonte(linha: LinhaDoCadastro): string {
  if (typeof linha.naoVistoNaFonteDesde === 'string') {
    return `Não visto desde ${formatarData(linha.naoVistoNaFonteDesde)}`;
  }
  if (typeof linha.vistoNaFonteEm === 'string') {
    return `Visto em ${formatarData(linha.vistoNaFonteEm)}`;
  }
  return '—';
}

function grafias(linha: LinhaDoCadastro): string {
  return Array.isArray(linha.grafias) && linha.grafias.length > 0
    ? (linha.grafias as string[]).join(', ')
    : '—';
}

function protecao(linha: LinhaDoCadastro): string {
  if (linha.protegido !== true) return '—';
  return typeof linha.sugestaoDaFonte === 'string'
    ? `Protegido · a fonte sugere ${rotuloDosTipos(linha.sugestaoDaFonte)}`
    : 'Protegido';
}

const COLUNAS_DA_FONTE = [
  { campo: grafias, titulo: 'Grafias' },
  { campo: situacaoNaFonte, titulo: 'Na fonte' },
  { campo: protecao, titulo: 'Proteção', ordenarPor: 'protegido' },
] as const;

const TIPO_NA_FONTE_COLUNA = {
  campo: (l: LinhaDoCadastro) =>
    rotuloDosTipos(typeof l.tipoNaFonte === 'string' ? l.tipoNaFonte : null),
  titulo: 'Tipo na fonte',
  ordenarPor: 'tipoNaFonte',
} as const;

const CAMPO_GRAFIAS: CampoDeFormulario = {
  nome: 'grafias',
  rotulo: 'Grafias equivalentes',
  bloco: 'Dados',
  tipo: 'grafias',
  maxLength: 120,
  ajuda:
    'Uma por linha: como a fonte oficial e o documento do veículo escrevem este item (por exemplo, VW e VOLKSWAGEN). O nome já vale como grafia.',
};

const CAMPO_PROTEGIDO: CampoDeFormulario = {
  nome: 'protegido',
  rotulo: 'Proteção',
  bloco: 'Dados',
  tipo: 'checkbox',
  rotuloDaMarcacao: 'Proteger contra a carga automática',
  ajuda:
    'Toda edição à mão protege o item. Desmarque e salve para a consulta diária voltar a atualizá-lo.',
};

const CAMPO_TIPO_NA_FONTE: CampoDeFormulario = {
  nome: 'tipoNaFonte',
  rotulo: 'Tipo na fonte oficial',
  bloco: 'Dados',
  maxLength: 120,
  ajuda:
    'Como o registro do veículo classifica este item (MICROONIBUS, ONIBUS). A consulta diária preenche; não é o tipo do TEG.',
};

const OBSERVACOES: CampoDeFormulario = {
  nome: 'observacoes',
  rotulo: 'Observações',
  bloco: 'Observações',
  tipo: 'textarea',
  maxLength: 1000,
};

const ORDEM: CampoDeFormulario = {
  nome: 'ordem',
  rotulo: 'Ordem',
  bloco: 'Dados',
  tipo: 'number',
  min: 0,
  max: 9999,
  ajuda: 'Quanto menor o número, mais acima o item aparece nas listas de escolha.',
};

const campoNome = (rotulo = 'Nome'): CampoDeFormulario => ({
  nome: 'nome',
  rotulo,
  bloco: 'Dados',
  obrigatorio: true,
  maxLength: 120,
});

const DE_CADASTROS = {
  grupo: 'cadastros' as const,
  hub: CADASTROS,
  rotuloDoHub: 'Cadastros & Domínios',
  colunasDeControle: true,
};

export const DEFINICOES_DE_CADASTRO: readonly DefinicaoDoCadastro[] = [
  {
    ...DE_CADASTROS,
    chave: 'marcas-veiculo',
    caminho: `${CADASTROS}/marcas-veiculo`,
    titulo: 'Marcas de Veículo',
    descricao: 'Fabricantes aceitos no cadastro de veículos da frota.',
    nomeDaEntidade: 'Marca',
    genero: 'a',
    integracaoComFonte: true,
    colunas: [NOME, ...COLUNAS_DA_FONTE],
    campos: [campoNome(), CAMPO_GRAFIAS, CAMPO_PROTEGIDO, ORDEM, OBSERVACOES],
  },
  {
    ...DE_CADASTROS,
    chave: 'modelos-veiculo',
    caminho: `${CADASTROS}/modelos-veiculo`,
    titulo: 'Modelos de Veículo',
    descricao: 'Modelos de cada fabricante.',
    nomeDaEntidade: 'Modelo',
    genero: 'o',
    integracaoComFonte: true,
    colunas: [
      NOME,
      { campo: 'marcaNome', titulo: 'Marca', ordenarPor: 'marcaNome' },
      TIPO_NA_FONTE_COLUNA,
      ...COLUNAS_DA_FONTE,
    ],
    campos: [
      {
        nome: 'marcaId',
        rotulo: 'Marca',
        bloco: 'Dados',
        obrigatorio: true,
        tipo: 'marca',
      },
      campoNome(),
      CAMPO_TIPO_NA_FONTE,
      CAMPO_GRAFIAS,
      CAMPO_PROTEGIDO,
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'versoes-veiculo',
    caminho: `${CADASTROS}/versoes-veiculo`,
    titulo: 'Versões de Veículo',
    descricao: 'Versões de cada modelo (por exemplo, Sprinter 415 CDI).',
    nomeDaEntidade: 'Versão',
    genero: 'a',
    situacaoFeminina: true,
    integracaoComFonte: true,
    colunas: [
      NOME,
      { campo: 'modeloNome', titulo: 'Modelo', ordenarPor: 'modeloNome' },
      { campo: 'marcaNome', titulo: 'Marca', ordenarPor: 'marcaNome' },
      TIPO_NA_FONTE_COLUNA,
      ...COLUNAS_DA_FONTE,
    ],
    campos: [
      {
        nome: 'modeloId',
        rotulo: 'Modelo',
        bloco: 'Dados',
        obrigatorio: true,
        tipo: 'modelo',
      },
      campoNome(),
      CAMPO_TIPO_NA_FONTE,
      CAMPO_GRAFIAS,
      CAMPO_PROTEGIDO,
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'tipos-veiculo',
    caminho: `${CADASTROS}/tipos-veiculo`,
    titulo: 'Tipos de Veículo',
    descricao: 'Categorias de veículo e a faixa de lugares de cada uma.',
    nomeDaEntidade: 'Tipo de veículo',
    genero: 'o',
    colunas: [NOME, { campo: lugares, titulo: 'Lugares', ordenarPor: 'capacidadeMin' }],
    campos: [
      campoNome(),
      {
        nome: 'capacidadeMin',
        rotulo: 'Lugares (mínimo)',
        bloco: 'Dados',
        tipo: 'number',
        min: 0,
        max: 200,
      },
      {
        nome: 'capacidadeMax',
        rotulo: 'Lugares (máximo)',
        bloco: 'Dados',
        tipo: 'number',
        min: 0,
        max: 200,
        ajuda: 'Deixe em branco quando a categoria não tem limite superior.',
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'cores-veiculo',
    caminho: `${CADASTROS}/cores-veiculo`,
    titulo: 'Cores de Veículo',
    descricao: 'Cores aceitas no cadastro da frota.',
    nomeDaEntidade: 'Cor',
    genero: 'a',
    colunas: [NOME, { campo: cor, titulo: 'Cor', ordenarPor: 'cor' }],
    campos: [
      campoNome(),
      {
        nome: 'cor',
        rotulo: 'Cor',
        bloco: 'Dados',
        maxLength: 7,
        ajuda: 'No formato #RRGGBB, por exemplo #FFD700.',
      },
      ORDEM,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'tipos-unidade-escolar',
    caminho: `${CADASTROS}/tipos-unidade-escolar`,
    titulo: 'Tipos de Unidade Escolar',
    descricao: 'Siglas da rede municipal e o que cada uma significa.',
    nomeDaEntidade: 'Tipo de unidade escolar',
    genero: 'o',
    colunas: [NOME, { campo: 'sigla', titulo: 'Sigla', ordenarPor: 'sigla' }],
    campos: [
      campoNome(),
      { nome: 'sigla', rotulo: 'Sigla', bloco: 'Dados', obrigatorio: true, maxLength: 12 },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'categorias-cnh',
    caminho: `${CADASTROS}/categorias-cnh`,
    titulo: 'Categorias CNH',
    descricao: 'Categorias de habilitação aceitas para condutor.',
    nomeDaEntidade: 'Categoria',
    genero: 'a',
    // O protótipo avisa que marcar "Exige EAR" bloqueia condutor sem o curso na
    // vinculação à OS. Nenhuma regra do sistema lê essa marcação hoje, e a tela
    // não afirma o que não vale (mesmo critério da coluna `Saúde`).
    aviso:
      'A marcação "Exige EAR" é só cadastro: nenhuma validação do sistema a consulta ainda, então ela não bloqueia condutor por si.',
    colunas: [
      { campo: 'codigo', titulo: 'Nome', ordenarPor: 'codigo' },
      { campo: 'descricao', titulo: 'Descrição', ordenarPor: 'descricao' },
      { campo: 'exigeEar', titulo: 'EAR', ordenarPor: 'exigeEar' },
    ],
    campos: [
      { nome: 'codigo', rotulo: 'Categoria', bloco: 'Dados', obrigatorio: true, maxLength: 10 },
      { nome: 'descricao', rotulo: 'Descrição', bloco: 'Dados', maxLength: 200 },
      {
        nome: 'exigeEar',
        rotulo: 'Exige EAR',
        bloco: 'Dados',
        tipo: 'checkbox',
        rotuloDaMarcacao: 'Exige observação de atividade remunerada (EAR)',
      },
      ORDEM,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'tipos-vinculo',
    caminho: `${CADASTROS}/tipos-vinculo`,
    titulo: 'Tipos de Vínculo Empregatício',
    descricao: 'Vínculos possíveis entre pessoa e credenciado.',
    nomeDaEntidade: 'Tipo de vínculo',
    genero: 'o',
    colunas: [NOME],
    campos: [campoNome(), ORDEM, OBSERVACOES],
  },
  {
    ...DE_CADASTROS,
    chave: 'cursos-obrigatorios',
    caminho: `${CADASTROS}/cursos-obrigatorios`,
    titulo: 'Cursos Obrigatórios',
    descricao: 'Cursos exigidos de condutor e monitor, com carga horária e validade.',
    nomeDaEntidade: 'Curso',
    genero: 'o',
    colunas: [
      NOME,
      {
        campo: (l) => (typeof l.cargaHorariaMin === 'number' ? `${l.cargaHorariaMin}h` : '—'),
        titulo: 'Carga',
        ordenarPor: 'cargaHorariaMin',
      },
      {
        campo: (l) =>
          typeof l.validadePadraoAnos === 'number'
            ? `${l.validadePadraoAnos} ${l.validadePadraoAnos === 1 ? 'ano' : 'anos'}`
            : '—',
        titulo: 'Validade',
        ordenarPor: 'validadePadraoAnos',
      },
      {
        campo: rotuloDe(ROTULO_DO_PUBLICO, 'publicoAlvo'),
        titulo: 'Público',
        ordenarPor: 'publicoAlvo',
      },
    ],
    campos: [
      campoNome(),
      {
        nome: 'cargaHorariaMin',
        rotulo: 'Carga horária (horas)',
        bloco: 'Dados',
        tipo: 'number',
        min: 0,
        max: 2000,
      },
      {
        nome: 'validadePadraoAnos',
        rotulo: 'Validade (anos)',
        bloco: 'Dados',
        tipo: 'number',
        min: 0,
        max: 50,
      },
      {
        nome: 'publicoAlvo',
        rotulo: 'Público',
        bloco: 'Dados',
        obrigatorio: true,
        tipo: 'select',
        opcoes: emOpcoes(ROTULO_DO_PUBLICO),
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'tipos-ocorrencia',
    caminho: `${CADASTROS}/tipos-ocorrencia`,
    titulo: 'Tipos de Ocorrência',
    descricao: 'Ocorrências que a fiscalização registra, com a gravidade de cada uma.',
    nomeDaEntidade: 'Tipo de ocorrência',
    genero: 'o',
    colunas: [
      NOME,
      {
        campo: rotuloDe(ROTULO_DA_GRAVIDADE, 'severidade'),
        titulo: 'Gravidade',
        ordenarPor: 'severidade',
      },
    ],
    campos: [
      campoNome(),
      {
        nome: 'severidade',
        rotulo: 'Gravidade',
        bloco: 'Dados',
        obrigatorio: true,
        tipo: 'select',
        opcoes: emOpcoes(ROTULO_DA_GRAVIDADE),
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'motivos-cancelamento-os',
    caminho: `${CADASTROS}/motivos-cancelamento-os`,
    titulo: 'Motivos de Cancelamento de OS',
    descricao: 'Por que uma ordem de serviço pode ser cancelada.',
    nomeDaEntidade: 'Motivo',
    genero: 'o',
    aviso: 'A marcação "Exige aprovação" é só cadastro: o cancelamento de OS ainda não a consulta.',
    colunas: [
      NOME,
      { campo: 'exigeAprovacao', titulo: 'Exige aprovação', ordenarPor: 'exigeAprovacao' },
    ],
    campos: [
      campoNome(),
      {
        nome: 'exigeAprovacao',
        rotulo: 'Exige aprovação',
        bloco: 'Dados',
        tipo: 'checkbox',
        rotuloDaMarcacao: 'O cancelamento por este motivo exige aprovação',
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'motivos-substituicao',
    caminho: `${CADASTROS}/motivos-substituicao`,
    titulo: 'Motivos de Substituição de Recurso',
    descricao: 'Por que veículo, condutor ou monitor pode ser substituído.',
    nomeDaEntidade: 'Motivo',
    genero: 'o',
    colunas: [
      NOME,
      {
        campo: rotuloDe(ROTULO_DO_RECURSO, 'recurso'),
        titulo: 'Aplica-se a',
        ordenarPor: 'recurso',
      },
    ],
    campos: [
      campoNome(),
      {
        nome: 'recurso',
        rotulo: 'Aplica-se a',
        bloco: 'Dados',
        obrigatorio: true,
        tipo: 'select',
        opcoes: emOpcoes(ROTULO_DO_RECURSO),
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    ...DE_CADASTROS,
    chave: 'tipos-publicacao',
    caminho: `${CADASTROS}/tipos-publicacao`,
    titulo: 'Tipos de Publicação de Documento',
    descricao: 'Meios oficiais de publicação.',
    nomeDaEntidade: 'Tipo de publicação',
    genero: 'o',
    colunas: [
      NOME,
      { campo: 'veiculoPublicacao', titulo: 'Meio de publicação', ordenarPor: 'veiculoPublicacao' },
    ],
    campos: [
      campoNome(),
      {
        nome: 'veiculoPublicacao',
        rotulo: 'Meio de publicação',
        bloco: 'Dados',
        maxLength: 120,
        ajuda: 'Onde o ato é publicado, por exemplo o Diário Oficial.',
      },
      ORDEM,
      OBSERVACOES,
    ],
  },
  {
    chave: 'seguradoras',
    grupo: 'referencia',
    caminho: `${REFERENCIA}/seguradoras`,
    hub: REFERENCIA,
    rotuloDoHub: 'Cadastros de Referência',
    titulo: 'Seguradoras',
    descricao: 'Empresas seguradoras citadas pelas apólices dos veículos.',
    nomeDaEntidade: 'Seguradora',
    genero: 'a',
    colunasDeControle: false,
    comDetalhe: true,
    situacaoFeminina: true,
    colunas: [
      NOME,
      { campo: 'razaoSocial', titulo: 'Razão Social', ordenarPor: 'razaoSocial' },
      { campo: 'cnpj', titulo: 'CNPJ', ordenarPor: 'cnpj' },
      { campo: 'susep', titulo: 'SUSEP', ordenarPor: 'susep' },
    ],
    campos: [
      { nome: 'nome', rotulo: 'Nome', bloco: 'Identificação', obrigatorio: true, maxLength: 120 },
      {
        nome: 'cnpj',
        rotulo: 'CNPJ',
        bloco: 'Identificação',
        ajuda: '00.000.000/0000-00',
        maxLength: 18,
      },
      {
        nome: 'razaoSocial',
        rotulo: 'Razão Social',
        bloco: 'Identificação',
        obrigatorio: true,
        maxLength: 200,
      },
      { nome: 'nomeFantasia', rotulo: 'Nome Fantasia', bloco: 'Identificação', maxLength: 200 },
      {
        nome: 'susep',
        rotulo: 'SUSEP',
        bloco: 'Identificação',
        ajuda: 'Registro na Superintendência de Seguros Privados.',
        maxLength: 40,
      },
      { nome: 'telefone', rotulo: 'Telefone', bloco: 'Identificação', maxLength: 40 },
      { nome: 'email', rotulo: 'E-mail', bloco: 'Identificação', tipo: 'email', maxLength: 200 },
      { nome: 'site', rotulo: 'Site', bloco: 'Identificação', ajuda: 'https://…', maxLength: 200 },
      {
        nome: 'ativo',
        rotulo: 'Situação',
        rotuloDaMarcacao: 'Seguradora ativa',
        bloco: 'Identificação',
        tipo: 'checkbox',
      },
      OBSERVACOES,
    ],
    detalhe: {
      titulo: 'Dados da seguradora',
      campos: [
        { rotulo: 'CNPJ', campo: 'cnpj' },
        { rotulo: 'Razão Social', campo: 'razaoSocial' },
        { rotulo: 'SUSEP', campo: 'susep' },
        { rotulo: 'Nome Fantasia', campo: 'nomeFantasia' },
        { rotulo: 'Telefone', campo: 'telefone' },
        { rotulo: 'E-mail', campo: 'email' },
        { rotulo: 'Site', campo: 'site' },
        { rotulo: 'Observações', campo: 'observacoes' },
      ],
    },
  },
  {
    chave: 'escolas',
    grupo: 'referencia',
    caminho: `${REFERENCIA}/escolas`,
    hub: REFERENCIA,
    rotuloDoHub: 'Cadastros de Referência',
    titulo: 'Escolas',
    descricao: 'Unidades escolares com código EOL, citadas por demandas e ordens de serviço.',
    nomeDaEntidade: 'Escola',
    genero: 'a',
    colunasDeControle: false,
    somenteConsulta: true,
    comDetalhe: true,
    situacaoFeminina: true,
    aviso: 'O cadastro de escolas vem do EOL e não se altera por aqui.',
    colunas: [
      { campo: 'codigoEol', titulo: 'Código EOL', ordenarPor: 'codigoEol' },
      NOME,
      { campo: 'tipo', titulo: 'Tipo', ordenarPor: 'tipo' },
      { campo: 'dre', titulo: 'DRE', ordenarPor: 'dre' },
      { campo: 'bairro', titulo: 'Bairro', ordenarPor: 'bairro' },
    ],
    campos: [],
    detalhe: {
      titulo: 'Endereço e Contato',
      campos: [
        { rotulo: 'Código EOL', campo: 'codigoEol' },
        { rotulo: 'Tipo de Unidade', campo: 'tipo' },
        {
          rotulo: 'DRE',
          campo: (l) =>
            [l.dre, l.dreNome]
              .filter((parte) => typeof parte === 'string' && parte !== '')
              .join(' — ') || '—',
        },
        { rotulo: 'CEP', campo: 'cep' },
        {
          rotulo: 'Endereço',
          campo: (l) =>
            [l.endereco, l.numero, l.complemento]
              .filter((parte) => typeof parte === 'string' && parte !== '')
              .join(', ') || '—',
        },
        { rotulo: 'Bairro', campo: 'bairro' },
        {
          rotulo: 'Cidade/UF',
          campo: (l) =>
            [l.cidade, l.uf]
              .filter((parte) => typeof parte === 'string' && parte !== '')
              .join(' / ') || '—',
        },
        { rotulo: 'Telefone', campo: 'telefone' },
        { rotulo: 'E-mail', campo: 'email' },
        { rotulo: 'Diretor', campo: 'diretor' },
        { rotulo: 'Observações', campo: 'observacoes' },
      ],
    },
  },
];

export function definicaoDoCadastro(chave: ChaveDeCadastro): DefinicaoDoCadastro {
  const definicao = DEFINICOES_DE_CADASTRO.find((d) => d.chave === chave);
  if (!definicao) throw new Error(`Cadastro sem definição: ${chave}`);
  return definicao;
}

export function definicaoPorCaminho(caminho: string): DefinicaoDoCadastro | undefined {
  return DEFINICOES_DE_CADASTRO.find((d) => d.caminho === caminho);
}
